import { Info } from 'lucide-react';
import { Popover } from '../../../components/Shared/Popover';
import { useTranslation } from '../../../i18n';
import { sumNet, type BreakdownPosition } from './splitOfferPositions';
import { billingKindLabelKey, formatBillingRule } from '../../../utils/billingKind';
import { numberLocale } from '../../../utils/locale';

interface BreakdownGroup {
  heading?: string;
  positions: BreakdownPosition[];
}

interface OfferAmountBreakdownProps {
  intro: string;
  groups: BreakdownGroup[];
  total: number;
  formatAmount: (value: number) => string;
}

/**
 * Info button next to an amount that opens the offer positions it is summed from, so the
 * user can see why the flat rate and the offer total differ (FAFD-15). Intro and total
 * stay fixed; only the list scrolls, with subtotals in the sticky group headings.
 */
export function OfferAmountBreakdown({ intro, groups, total, formatAmount }: OfferAmountBreakdownProps) {
  const { t } = useTranslation();
  const visibleGroups = groups.filter((g) => g.positions.length > 0);
  // why: kind + months say when the invoice bills the position (FAFD-27), the interval
  // text above only describes the work.
  const billingLine = (p: BreakdownPosition) => {
    const rule = formatBillingRule(p.kind, p.billingRule, numberLocale(), t('offers:billing.inArrearsShort'));
    const kind = t(billingKindLabelKey(p.kind));
    return rule ? `${kind} · ${rule}` : kind;
  };

  return (
    <Popover
      label={t('objects:activeOffer.breakdown.show')}
      trigger={<Info className="w-3.5 h-3.5" strokeWidth={2} />}
    >
      <div className="shrink-0 border-b border-slate-100 px-3 pt-2.5 pb-2 text-slate-500">{intro}</div>

      <div className="min-h-0 flex-1 overflow-y-auto px-3 pb-2">
        {visibleGroups.length === 0 ? (
          <div className="pt-2 text-slate-500">{t('objects:activeOffer.breakdown.empty')}</div>
        ) : (
          visibleGroups.map((group, index) => (
            <div key={group.heading ?? index}>
              {group.heading && (
                <div className="sticky top-0 -mx-3 mt-2 mb-1.5 flex justify-between gap-3 border-y border-slate-100 bg-slate-50 px-3 py-1 text-[10px] font-medium uppercase tracking-wider text-slate-500">
                  <span>{group.heading}</span>
                  <span className="shrink-0 tabular-nums">{formatAmount(sumNet(group.positions))}</span>
                </div>
              )}
              <div className={`space-y-1.5 ${group.heading ? '' : 'pt-2'}`}>
                {group.positions.map((p) => (
                  <div key={p.key} className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="line-clamp-2 text-slate-900">{p.title}</div>
                      <div className="line-clamp-2 text-[11px] text-slate-500">
                        {p.interval || t('objects:activeOffer.breakdown.noInterval')}
                      </div>
                      <div className="text-[11px] text-slate-500">{billingLine(p)}</div>
                    </div>
                    <div className="shrink-0 tabular-nums text-slate-900">{formatAmount(p.netAmount)}</div>
                  </div>
                ))}
              </div>
            </div>
          ))
        )}
      </div>

      <div className="flex shrink-0 justify-between gap-3 border-t border-slate-200 px-3 py-2 font-semibold text-slate-900">
        <span>{t('objects:activeOffer.breakdown.sum')}</span>
        <span className="tabular-nums">{formatAmount(total)}</span>
      </div>
    </Popover>
  );
}
